<template>
  <validation-observer :ref="id + '-observer'"
                       v-slot="{ invalid, changed, dirty, handleSubmit }" slim>

    <form @submit.prevent="handleSubmit(onSubmit)">

      <div class="row">

        <div class="col-md-8">

          <div class="form-group row">

            <label :for="id + '-title'"
                   class="col-sm-2 col-form-label text-right">{{ $t('notification.title') }}</label>

            <div class="col-sm-10">
              <validation-provider :vid="id + '-title'" name="title" rules="max:255"
                                   v-slot="{ classes, errors }" slim>
                <div>
                  <b-form-input :id="id + '-title'"
                                size="sm" maxlength="255"
                                :class="classes"
                                v-model="value.title"/>
                  <span class="invalid-feedback">{{ errors[0] }}</span>
                </div>
              </validation-provider>
            </div>

          </div>

          <div class="form-group row">

            <label :for="id + '-text'"
                   class="col-sm-2 col-form-label text-right required">{{ $t('notification.text') }}</label>

            <div class="col-sm-10">
              <validation-provider :vid="id + '-text'" name="text" rules="required"
                                   v-slot="{ classes, errors }" slim>
                <div>
                  <b-form-textarea :id="id + '-text'"
                                   size="sm" rows="6"
                                   :class="classes"
                                   v-model="value.text"/>
                  <span class="invalid-feedback">{{ errors[0] }}</span>
                </div>
              </validation-provider>
            </div>

          </div>

        </div>

        <div class="col-md-4">

          <div class="form-group row">

            <label :for="id + '-audience'"
                   class="col-sm-4 col-form-label text-right required">{{ $t('notification.audience') }}</label>

            <div class="col-sm-8">
              <e-form-select :id="id + '-audience'"
                             name="audience" size="sm" rules="required"
                             hide-placeholder
                             v-model="value.audience">
                <option v-for="audience in audiences" :key="audience" :value="audience">
                  {{ $t('notification.audience.' + audience) }}
                </option>
              </e-form-select>
            </div>

          </div>

          <div class="form-group row">

            <label :for="id + '-validFrom'"
                   class="col-sm-4 col-form-label text-right">{{ $t('notification.validFrom') }}</label>

            <div class="col-sm-8">
              <b-form-datepicker :id="id + '-validFrom'"
                                 size="sm"
                                 reset-button
                                 :label-reset-button="$t('notification.date.reset')"
                                 :placeholder="$t('notification.validFrom.empty')"
                                 :locale="$i18n.locale"
                                 :max="validUntilDate"
                                 v-model="validFromDate"/>
            </div>

          </div>

          <div class="form-group row">

            <label :for="id + '-validUntil'"
                   class="col-sm-4 col-form-label text-right">{{ $t('notification.validUntil') }}</label>

            <div class="col-sm-8">
              <b-form-datepicker :id="id + '-validUntil'"
                                 size="sm"
                                 reset-button
                                 :label-reset-button="$t('notification.date.reset')"
                                 :placeholder="$t('notification.validUntil.empty')"
                                 :locale="$i18n.locale"
                                 :min="validFromDate"
                                 v-model="validUntilDate"/>
            </div>

          </div>

        </div>

      </div>

      <div class="row">

        <div class="col-md-6">
          <div v-if="resetDone" class="text-muted small pt-1">{{ $t('notification.resetRead.done') }}</div>
        </div>

        <div class="col-md-6">
          <div class="form-group row justify-content-end">
            <div class="col-sm-3" v-if="canResetRead()">
              <button :id="id + '-resetReadButton'"
                      type="button"
                      class="btn btn-sm btn-block btn-outline-secondary"
                      @click.prevent="resetRead()">{{ $t('notification.resetRead') }}</button>
            </div>
            <div class="col-sm-3" v-if="canModify()">
              <button :id="id + '-saveButton'"
                      type="submit"
                      class="btn btn-sm btn-block btn-outline-primary"
                      :disabled="!(changed || dirty || datesChanged) || invalid">{{ $t('controls.save') }}</button>
            </div>
            <div class="col-sm-3" v-if="canDelete()">
              <button :id="id + '-deleteButton'"
                      type="button"
                      class="btn btn-sm btn-block btn-outline-danger"
                      @click.prevent="deleteEntity()">{{ $t('controls.delete') }}</button>
            </div>
          </div>
        </div>

      </div>

    </form>

  </validation-observer>
</template>

<script>

import service from "@/services/adminNotification.service";
import {log, ValidationObserver, ValidationProvider} from "@emons/emons-vue"

export default {
  name: "NotificationView",
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    id: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      audiences: ['ALL', 'CUSTOMERS', 'EMPLOYEES'],
      // the plain datepickers are not part of the validation observer
      datesChanged: false,
      resetDone: false
    }
  },
  computed: {
    validFromDate: {
      get() {
        return this.dateOf(this.value.validFrom)
      },
      set(date) {
        this.setDate('validFrom', date, '00:00:00')
      }
    },
    validUntilDate: {
      get() {
        return this.dateOf(this.value.validUntil)
      },
      set(date) {
        this.setDate('validUntil', date, '23:59:59')
      }
    }
  },
  mounted() {
    log('debug', 'mounted()...')
    if (this.value._links)
      this.getEntityDetails()
  },
  methods: {
    isNew() {
      return !this?.value?._links?.self
    },
    canDelete() {
      return this.isNew() || !!this?.value?._links?.delete
    },
    canModify() {
      return this.isNew() || !!this?.value?._links?.modify
    },
    canResetRead() {
      return !!this?.value?._links?.resetRead
    },
    dateOf(dateTime) {
      return dateTime ? dateTime.substring(0, 10) : null
    },
    setDate(property, date, time) {
      if ((date || null) === this.dateOf(this.value[property]))
        return

      this.value[property] = date ? date + 'T' + time : null
      this.datesChanged = true
    },
    getEntityDetails: function () {
      this.$emit('enableSpinner');
      service
          .getEntityDetails(this.value._links.self.href)
          .then(response => {
            Object.assign(this.value, response.data)
            this.$emit('disableSpinner')
          })
          .catch(error => {
            this.$emit('disableSpinner')
            this.$eventBus.$emit('backend-error', error)
          })
    },
    deleteEntity() {
      if (this.isNew()) {
        this.$emit('delete')
        return
      }
      this.$emit('enableSpinner');
      service.delete(this.value)
          .then(() => {
            this.$emit('delete')
            this.$emit('disableSpinner')
          })
          .catch(error => {
            this.$emit('disableSpinner')
            this.$eventBus.$emit('backend-error', error)
          })
    },
    resetRead() {
      this.$emit('enableSpinner');
      service.resetRead(this.value)
          .then(() => {
            this.resetDone = true
            this.$emit('disableSpinner')
          })
          .catch(error => {
            this.$emit('disableSpinner')
            this.$eventBus.$emit('backend-error', error)
          })
    },
    onSubmit() {
      this.$emit('enableSpinner');
      service.save(this.value)
          .then(response => {
            this.$refs[this.id + '-observer'].reset()
            this.datesChanged = false
            this.$emit('save', response.data)
            this.$emit('disableSpinner')
          })
          .catch(error => {
            this.$emit('disableSpinner')
            if (error.response && error.response.data && error.response.data.errors) {
              this.$refs[this.id + '-observer'].setErrors(error.response.data.errors)
            } else {
              this.$eventBus.$emit('backend-error', error)
            }
          })
    }
  }
}
</script>

<style scoped>

</style>
